import Image from 'next/image'
import { type ReactNode } from 'react'
import {
  FormattedMessage,
  FormattedNumber,
  type IntlShape,
  useIntl,
} from 'react-intl'

import { type GetCarriersV1 } from '@redteclab/api/clients/bully'

import {
  type PublicGlobalConfig,
  useGlobalConfigContext,
} from '../../../../global-config'
import { TENANT } from '../../../../tenant'
import { assetUrlGetShippingCarrierLogo } from '../../../../url-handling'
import { useProductPageContext } from '../../productPageContext'

import { ProductDeliveryInfoExtraDetailsForCarrier } from './ProductDeliveryInfoExtraDetailsForCarrier'

type CellElementType = {
  content: ReactNode
  isWithinPreviousRowSpan?: boolean
  rowSpan?: number
}

type RowElementType = CellElementType[]

const getSortedFreeShippingCostThresholds = (
  carriers: GetCarriersV1[],
): number[] => {
  return carriers
    .map((carrier) => carrier.freeShippingThreshold)
    .filter((threshold): threshold is number => threshold !== null)
    .filter((element, index, array) => {
      return array.indexOf(element) === index
    })
    .sort((firstElement, secondElement) => firstElement - secondElement)
}

const getHeaderRowSae = (
  tenant: TENANT,
  carriers: GetCarriersV1[],
  freeShippingCostThresholds: number[],
): RowElementType => {
  const formattedShippingCostThresholds = freeShippingCostThresholds.map(
    (shippingCostThreshold) => (
      <FormattedNumber
        currency={carriers[0].currency}
        key={shippingCostThreshold}
        style="currency"
        value={shippingCostThreshold}
      />
    ),
  )

  const headerRows: RowElementType = [
    {
      content: '',
    },
    {
      content: (
        <FormattedMessage
          id="buyBox.delivery.below.price.label"
          values={{ minAmount: formattedShippingCostThresholds[0] }}
        />
      ),
    },
  ]

  for (const formattedShippingCostThreshold of formattedShippingCostThresholds) {
    headerRows.push({
      content: (
        <FormattedMessage
          id="buyBox.delivery.from.price.label"
          values={{ minAmount: formattedShippingCostThreshold }}
        />
      ),
    })
  }

  /**
   * BE has an additional column for the shipping method - "modalities" e.g. eveining, pickup, etc.
   */
  if (tenant === TENANT.BE) {
    headerRows.push({
      content: <FormattedMessage id="buyBox.delivery.shippingMethod.label" />,
    })
  }

  return headerRows
}

/**
 * this function calculates the row span for the carrier group.
 * subsequent same groups are considered for row span calculation.
 * given [foo, foo, foo, bar] -> for (0, foo) => 3
 * given [foo, bar, foo, foo] -> for (2, foo) => 2 (only subsequent rows are considered for row span calculation)
 * given [foo, bar, baz] -> for (1, baz) -> 1
 */
const calculateRowSpanForGroup = (
  carriers: GetCarriersV1[],
  startIndex: number,
  group?: string,
): number => {
  let rowSpan = 0
  for (
    let currentIndex = startIndex;
    currentIndex < carriers.length;
    currentIndex += 1
  ) {
    if (carriers[currentIndex].group !== group) {
      break
    }

    if (carriers[currentIndex].group === group) {
      rowSpan += 1
    }
  }

  return rowSpan
}

const getBodyRowsSae = ({
  carriers,
  freeShippingCostThresholds,
  globalConfig,
  tenant,
}: {
  carriers: GetCarriersV1[]
  freeShippingCostThresholds: number[]
  globalConfig: PublicGlobalConfig
  intl: IntlShape
  tenant: TENANT
}): RowElementType[] => {
  const { assetsPrefix, language } = globalConfig

  return carriers.map((carrier, index: number) => {
    const previousCarrier = index > 0 ? carriers[index - 1] : null

    const src = assetUrlGetShippingCarrierLogo({
      assetsPrefix,
      group: carrier.group,
      language,
    })

    const carrierBaseRow: CellElementType[] = [
      {
        content: (
          <div className="relative mx-4 h-full">
            <Image
              alt={carrier.group}
              className="object-contain"
              fill
              src={src}
              unoptimized
            />
          </div>
        ),
        /**
         * if carrier group is same on previous, means previous will have row span, so this one must be ignored.
         */
        isWithinPreviousRowSpan: carrier.group === previousCarrier?.group,
        /**
         * if carrier group is same on subsequent rows we must show the logo only once with row span.
         * e.g.
         * [dhl, hermes, ups] -> each row show logo in 1st col
         * [dhl, dhl, hermes, ups] -> dhl 1st col will have row span of 2 so logo spans 2 rows, second row's 1st col will be skipped
         */
        rowSpan: calculateRowSpanForGroup(carriers, index, carrier.group),
      },
      {
        content: (
          <FormattedNumber
            currency={carrier.currency}
            style="currency"
            value={carrier.shippingCost}
          />
        ),
      },
    ]

    for (const freeShippingCostThreshold of freeShippingCostThresholds) {
      if (
        carrier.freeShippingThreshold === null ||
        carrier.freeShippingThreshold > freeShippingCostThreshold
      ) {
        carrierBaseRow.push({
          content: (
            <FormattedNumber
              currency={carrier.currency}
              style="currency"
              value={carrier.shippingCost}
            />
          ),
        })
      } else {
        carrierBaseRow.push({
          content: (
            <span className="font-medium text-dark-success">
              <FormattedMessage id="buyBox.delivery.noCosts.text" />
            </span>
          ),
        })
      }
    }

    /**
     * belgium has an additional column for the shipping method - "modalities" e.g. eveining, pickup, etc.
     */
    if (tenant === TENANT.BE) {
      carrierBaseRow.push({
        content: (
          <span>
            <ProductDeliveryInfoExtraDetailsForCarrier carrier={carrier} />
          </span>
        ),
      })
    }

    return carrierBaseRow
  })
}

const sortCarriersByPosition = (carriers: GetCarriersV1[]): GetCarriersV1[] => {
  return carriers.sort((first, second) => {
    if (first.position && second.position) {
      return first.position - second.position
    }

    return 0
  })
}

export const usePrepareDeliveryTableDataSae = (): {
  bodyRows: RowElementType[]
  headerRow: RowElementType
} => {
  const globalConfig = useGlobalConfigContext()
  const {
    shipping: { saeShippingCosts },
  } = useProductPageContext()
  const intl = useIntl()
  if (saeShippingCosts.carriers && saeShippingCosts.carriers.length > 0) {
    const carriers = sortCarriersByPosition(saeShippingCosts.carriers)

    const freeShippingCostThresholds =
      getSortedFreeShippingCostThresholds(carriers)

    const headerRow = getHeaderRowSae(
      globalConfig.tenant,
      carriers,
      freeShippingCostThresholds,
    )

    const bodyRows = getBodyRowsSae({
      carriers,
      freeShippingCostThresholds,
      globalConfig,
      intl,
      tenant: globalConfig.tenant,
    })

    return { bodyRows, headerRow }
  }

  return { bodyRows: [], headerRow: [] }
}
